import { Loader } from '@openx/components-core/lib/Loader/Loader.tsx';
import { ACL_TYPE } from '@openx/types/aclType.ts';
import type { DealParticipantApi, PdpConfigApi, PmpDealType } from '@openx/types/deal.ts';
import { pmpDealType } from '@openx/types/deal.ts';
import { UserRole } from '@openx/types/user.ts';
import { usePermissionContext } from '@openx/utils/permission/context/PermissionContext.tsx';
import { isAllowedByCompiledAcl } from '@openx/utils/permission/validators.ts';
import { AMAZON_DEMAND_PARTNER_ID, TTD_DEMAND_PARTNER_ID } from '@openx/utils/services/config.ts';
import { useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDealContext } from '../../utils/context/DealContext.tsx';
import { DemandPartnerType, type IParticipantFunctions } from '../../utils/participantFunctions.ts';

import { ParticipantDefault } from './ParticipantDefault.tsx';
import { ParticipantTTD } from './ParticipantTTD.tsx';
import type { ParticipantDefaultProps } from './types.ts';

function formatMediaSpendGoal(
  demandPartner: string,
  dealType: PmpDealType,
  pdpConfig?: PdpConfigApi | null,
  currency?: string
): string | undefined {
  if (demandPartner !== TTD_DEMAND_PARTNER_ID || dealType === pmpDealType.Guaranteed || !pdpConfig?.media_spend_goal)
    return undefined;

  const value = pdpConfig.media_spend_goal;
  const decimalPlaces = value.includes('.') ? value.split('.')[1].length : 0;

  return new Intl.NumberFormat('en-EN', {
    currency: currency ?? 'USD',
    style: 'currency',
    minimumFractionDigits: 2,
    maximumFractionDigits: Math.max(decimalPlaces, 2),
  }).format(+value);
}

export interface ParticipantProps {
  dealParticipant: Pick<DealParticipantApi, 'brand_ids' | 'buyer_ids' | 'demand_partner'>;
  mainBuyer: string | null;
  dealType: PmpDealType;
  participantFunctions: IParticipantFunctions;
  enableDv360InventoryPackage: boolean;
  pdp_config?: PdpConfigApi | null;
  currency?: string;
}

export function Participant({
  dealType,
  dealParticipant: { buyer_ids = {}, brand_ids = {}, demand_partner },
  mainBuyer,
  participantFunctions,
  enableDv360InventoryPackage,
  pdp_config,
  currency,
}: ParticipantProps) {
  const { t } = useTranslation();
  const { useIsAllowed } = usePermissionContext();
  const { isAllowed, accessUser } = useIsAllowed();
  const isOaInternal = !!accessUser?.roles.includes(UserRole.NETWORK_OA_INTERNAL);
  const isExternalUser = !isAllowed(isAllowedByCompiledAcl(ACL_TYPE.INTERNAL_FIELD_WRITE)) && !isOaInternal;
  const isAmazonDemandPartner = demand_partner === AMAZON_DEMAND_PARTNER_ID;

  const { useParticipantHooks } = useDealContext();

  const selectedBrandIds = useMemo(() => Object.keys(brand_ids), [brand_ids]);
  const selectedSeatIds = useMemo(() => Object.keys(buyer_ids), [buyer_ids]);

  const [
    { buyers, isBuyersLoading },
    { demandPartners, isDemandPartnersLoading, refetchDemandPartners },
    { brands, isBrandsLoading },
  ] = useParticipantHooks({ demandPartnerId: demand_partner, selectedBrandIds });

  const loading = isBuyersLoading || isDemandPartnersLoading || isBrandsLoading;

  const demandPartnerType = participantFunctions.getDemandPartnerType(demand_partner, dealType);

  const labels = useMemo(
    () => ({
      additionalBuyer: t('Additional Buyers'),
      brands: t('Brands'),
      demandPartner: t('DSP'),
      enableForDV360IP: t('Include in DV360 Inventory Package'),
      mainBuyer: t('Main Buyer'),
      seatIDs: t(demandPartnerType === DemandPartnerType.TTD ? 'DSP Buyer IDs' : 'DSP Seat IDs'),
    }),
    [t, demandPartnerType]
  );

  useEffect(() => {
    refetchDemandPartners?.();
  }, [refetchDemandPartners]);

  const getBuyerName = useCallback<(id: string | null) => string>(
    id => {
      if (!id) {
        return t('N / A');
      }

      if (isAmazonDemandPartner && isExternalUser) {
        return id;
      }

      const buyer = buyers[id];

      return buyer?.platform_seat_name || t('N / A');
    },
    [buyers, t, isAmazonDemandPartner, isExternalUser]
  );

  const demandPartnerValue = demandPartners[demand_partner]?.name;

  const mediaSpendGoalValue = formatMediaSpendGoal(demand_partner, dealType, pdp_config, currency);

  const props: ParticipantDefaultProps = {
    brands,
    demandPartnerType,
    demandPartnerValue,
    enableDv360InventoryPackage,
    getBuyerName,
    labels,
    mainBuyer,
    mediaSpendGoalValue,
    selectedBrandIds,
    selectedSeatIds,
  };

  const renderContent = () => {
    if (mainBuyer || mediaSpendGoalValue) {
      return <ParticipantTTD {...props}></ParticipantTTD>;
    }

    switch (demandPartnerType) {
      case DemandPartnerType.TTD:
        return <ParticipantTTD {...props}></ParticipantTTD>;
      case DemandPartnerType.XANDR:
      case DemandPartnerType.DV360:
      case DemandPartnerType.DEFAULT:
        return <ParticipantDefault {...props}></ParticipantDefault>;
    }
  };

  return loading ? <Loader /> : renderContent();
}
